/**
 * Review's rules, kept out of the components so `npm test` can reach them (日本-185).
 *
 * The surface has no DOM in the suite, so what lives here is everything that can be decided from
 * values alone: which key means what, whether a drag counts as a swipe, which images to show, and
 * what the chrome bar says. `libraryDrag.ts` and `menuPlacement.ts` are the same shape for the same
 * reason — the interesting bugs on this surface are in the arithmetic, not in the markup.
 */

import type { DayProgress, ReviewCard, ReviewCounts } from "@/lib/review";

// --- keys ----------------------------------------------------------------------------------------

/**
 * What a session can do. **Four directions** — left fail, right pass, up undo, down unreveal — plus
 * reveal and the actions the `Card ⋯` menu duplicates.
 *
 * The design's claim is that the keys and the phone gestures are the same mental model, so there is
 * one thing to learn rather than two. **`unreveal` is the one deliberate exception, and the reason
 * is the scroll** (Alex, 11 Aug 2026): un-revealing is only meaningful on the answer face, and a
 * revealed answer scrolls vertically — so the gesture that would mirror `↓` is the gesture that
 * reads a long card. The axis belongs to scrolling there, and unreveal is a key alone. It was
 * offered a button on the phone instead and Alex declined one; two steps of chrome to put an answer
 * away is worse than reaching for the key.
 *
 * Everything else still holds the rule: `↑` undo has its swipe on the question face, where nothing
 * scrolls.
 */
export type ReviewAction =
  | "reveal"
  | "unreveal"
  | "fail"
  | "pass"
  | "undo"
  | "edit"
  | "suspend"
  | "menu"
  | "info"
  | "tag"
  | "groups"
  | "end";

export interface KeyContext {
  /** Before the answer is shown, `←`/`→` do nothing: there is nothing yet to grade. */
  revealed: boolean;
}

/**
 * A keyboard event to an action, or null for "not ours".
 *
 * **Modified keys are never ours.** `Ctrl+R` reloads, `Cmd+E` is the browser's, and a reviewer that
 * swallowed them would be the most annoying possible bug. `metaKey` is accepted nowhere and checked
 * everywhere for that reason — the app names Windows keys in its hints (Alex, 7 Aug 2026) but must
 * not *steal* a Mac chord either.
 */
export function actionForKey(
  event: Pick<KeyboardEvent, "key" | "ctrlKey" | "metaKey" | "altKey" | "shiftKey">,
  context: KeyContext,
): ReviewAction | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;

  switch (event.key) {
    // **Space reveals, and then passes** (Alex, 9 Aug 2026). This reverses the caution it shipped
    // with — that a key meaning "show me" must not become "I knew it" — because in practice the
    // overwhelming majority of answers are passes, and one key you can hold a rhythm on is the
    // whole ergonomics of a 200-card session. `←` is still the only way to fail, so the dangerous
    // direction keeps needing a deliberate reach.
    case " ":
    case "Enter":
      return context.revealed ? "pass" : "reveal";
    // `1` fails and `2` passes, on the number row — the same two actions the buttons carry, for a
    // hand that is nowhere near the arrows.
    case "1":
      return context.revealed ? "fail" : "reveal";
    case "2":
      return context.revealed ? "pass" : "reveal";
    // **Both arrows reveal first.** Pressing `→` on a question used to do nothing, so the first
    // keystroke of every card was wasted; now the direction you were going to press anyway opens
    // the card, and the second press grades it. It cannot grade blind: the reveal consumes the
    // first press.
    case "ArrowLeft":
      return context.revealed ? "fail" : "reveal";
    case "ArrowRight":
      return context.revealed ? "pass" : "reveal";
    case "ArrowUp":
      return "undo";
    // **`↓` puts the answer away again** (Alex, 11 Aug 2026). It pairs with `↑` rather than merely
    // filling a spare key: the vertical axis goes *backwards*, at two granularities — `↑` un-answers
    // a card, `↓` un-reveals a step. On a question there is nothing to put away, so it is not ours.
    case "ArrowDown":
      return context.revealed ? "unreveal" : null;
    case "Escape":
      return "end";
    case "e":
    case "E":
      return "edit";
    case "!":
      return "suspend";
    case "i":
    case "I":
      return "info";
    case "t":
    case "T":
      return "tag";
    case ".":
      return "menu";
    // グループに追加 (日本-601). A Postgres write, so it is safe mid-sitting in a way a tag is not —
    // it cannot sync, so it cannot clear the undo queue the sitting is holding open.
    case "g":
    case "G":
      return "groups";
    default:
      return null;
  }
}

// --- swipes --------------------------------------------------------------------------------------

export const SWIPE_MIN_PX = 64;
/**
 * How far sideways a drag must go, and how much more sideways than vertical.
 *
 * The ratio is the load-bearing half. A reading surface on a phone scrolls vertically, so a drag
 * that is mostly down must never grade a card — being at 64px sideways is not enough on its own
 * when you have travelled 300px down.
 */
export const SWIPE_RATIO = 1.6;

export function swipeAction(dx: number, dy: number): "fail" | "pass" | null {
  if (Math.abs(dx) < SWIPE_MIN_PX) return null;
  if (Math.abs(dx) < Math.abs(dy) * SWIPE_RATIO) return null;
  return dx < 0 ? "fail" : "pass";
}

/**
 * A drag that is deliberately vertical — **the front's undo gesture, and nowhere else.**
 *
 * `swipeAction`'s guard turned ninety degrees, and it is doing the same job: a drag that is mostly
 * sideways must not undo a card any more than a drag that is mostly down may grade one.
 *
 * **Only the question face consults this**, which is what keeps it safe. A revealed answer scrolls
 * vertically, so on the back the vertical axis belongs to scrolling and nothing else (Alex, 11 Aug
 * 2026) — an unrevealed card is a single line in a box that does not scroll, so there is nothing
 * there for the gesture to be confused with. That is also why `"down"` has no meaning yet: the one
 * thing it could mean, un-revealing, only exists on the face this is not read on.
 */
export function verticalSwipe(dx: number, dy: number): "up" | "down" | null {
  if (Math.abs(dy) < SWIPE_MIN_PX) return null;
  if (Math.abs(dy) < Math.abs(dx) * SWIPE_RATIO) return null;
  return dy < 0 ? "up" : "down";
}

// --- images --------------------------------------------------------------------------------------

export type ImageMode = "main" | "extra" | "both";

export const IMAGE_MODE_KEY = "nihongonator.review.images";

/**
 * **Per device, not synced** — the design says so outright, and it is a real difference: the phone
 * wants one image, the ultrawide has room for two. `localStorage` for the same reason `logTabs.ts`
 * uses it, and an unreadable or nonsense value falls back rather than throwing.
 */
export function loadImageMode(storage: Pick<Storage, "getItem"> | null): ImageMode {
  try {
    const raw = storage?.getItem(IMAGE_MODE_KEY);
    return raw === "main" || raw === "extra" || raw === "both" ? raw : "both";
  } catch {
    return "both";
  }
}

export function saveImageMode(storage: Pick<Storage, "setItem"> | null, mode: ImageMode): void {
  try {
    storage?.setItem(IMAGE_MODE_KEY, mode);
  } catch {
    /* a private window with storage disabled is not a reason to fail a review */
  }
}

/**
 * Which images actually render, given what the reader asked for and what the note has.
 *
 * **The note wins silently** (frame `7b`). When the toggle asks for something the note does not
 * carry there is no placeholder, no empty box and no missing-media warning — those would be an
 * error message in the middle of a review, about a card that is working exactly as intended.
 *
 * **`extra` falls back to the main image; `main` does NOT fall back to the extra one** (日本-395,
 * Alex's wording: *"the inverse is not true for normal image mode"*). The asymmetry is the whole
 * point and it is not an oversight to tidy up:
 *
 * - Most notes carry one picture and it is in the **main** slot, so `追加` on such a card used to
 *   draw nothing at all. That is the complaint: a reader who prefers the extra image when there is
 *   one was being shown a blank card the rest of the time, and — because the empty case is silent
 *   by the rule above — with nothing on screen saying why.
 * - The other direction has no such reading. `主` is a reader saying *this* slot, and a card whose
 *   only picture sits in `追加` is one they deliberately switched away from. Falling back there
 *   would quietly overrule the toggle rather than satisfy it.
 *
 * So `追加` means *the extra picture, or the one this card has*, while `主` means *the main picture
 * and nothing else*. `both` is untouched: it already draws whatever exists.
 */
export function visibleImages(
  card: Pick<ReviewCard, "image" | "extraImage">,
  mode: ImageMode,
): string[] {
  const wanted =
    mode === "main"
      ? [card.image]
      : mode === "extra"
        ? [card.extraImage ?? card.image]
        : [card.image, card.extraImage];
  return wanted.filter((name): name is string => Boolean(name));
}

/**
 * **All three, always** (Alex, 9 Aug 2026).
 *
 * This replaces a rule that hid `Extra` until a note carrying one had been seen, on the grounds
 * that an option which can never do anything teaches you the control is broken. The trade is worse
 * the other way round: the segmented control changed width mid-session as soon as an extra image
 * turned up, and a setting that is *sticky per device* was being decided by whichever cards
 * happened to come first. It is a preference about what you want to see, not a report on the deck.
 *
 * `visibleImages` already makes the empty case silent — the note wins, with no placeholder and no
 * warning — so a mode the current card cannot satisfy costs nothing. Since 日本-395 that is a
 * statement about `主` only: `追加` now falls back to the main image, so it is very nearly always
 * satisfiable and the argument for offering it unconditionally is stronger, not weaker.
 */
export function imageModeOptions(): ImageMode[] {
  return ["main", "extra", "both"];
}

/**
 * What the segmented control prints for a mode.
 *
 * Separate from the value because **the value is persisted per device** (`loadImageMode`), so
 * translating it in place would silently reset every existing reader's choice to the default and
 * leave an unreadable string in `localStorage`. The same reason `ImageMode` is not an enum of
 * Japanese words.
 *
 * 主 and 両方 are the canvas's own; 追加 is the third and matches the `追加画像` field label the
 * answer face draws directly above this control, so the toggle and the slot it governs use one
 * word.
 */
export function imageModeLabel(mode: ImageMode): string {
  return { main: "主", extra: "追加", both: "両方" }[mode];
}

// --- the chrome bar ------------------------------------------------------------------------------

/**
 * `この席 84枚目` — where you are in **this sitting**, which is the only thing the header still
 * says about it (日本-389).
 *
 * It is what is left of the old progress bar, and demoting it to a caption is the whole point of
 * that ticket: `answered / (answered + remaining)` looked like progress and was not, because a
 * failed card comes back and grows the denominator, so the fill ran *backwards*. As a plain ordinal
 * it cannot lie — the 84th card you have looked at is the 84th card you have looked at, whatever
 * happens to the queue behind it. The bars above it answer the question that number was pretending
 * to.
 *
 * `+ 1` because it names the card on screen, not the ones behind it: a fresh sitting opens on
 * `この席 1枚目`, which is what frame `1a`'s day-start state draws.
 */
export function sittingPosition(counts: Pick<ReviewCounts, "answered">): string {
  return `この席 ${counts.answered + 1}枚目`;
}

// --- the day bars (日本-389) ----------------------------------------------------------------------

/** A fraction for a bar fill: 0–1, and never NaN on an empty day. */
function share(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.min(1, Math.max(0, part / whole));
}

export interface BarFills {
  /** One fill, ash. */
  cards: number;
  /** Two fills in this order, bone then vermilion. They are shares of the same track. */
  pass: number;
  fail: number;
}

/**
 * The widths the two tracks are drawn at.
 *
 * **Three fills and no third hue.** A pass is bone — the brightest ink the theme has — and a fail
 * is the accent, which already means *fail* two rows down on the same screen where 不可 is drawn in
 * it. The card bar stays ash, so the two bars read as different measures at a glance and the
 * brighter one is the one that moves. Green was tried in the design and dropped for exactly that:
 * it earned nothing the app's existing two could not say.
 */
export function dayBars(day: DayProgress): BarFills {
  return {
    cards: share(day.cardsDone, day.cardsTotal),
    pass: share(day.passes, day.reviewsTotal),
    fail: share(day.fails, day.reviewsTotal),
  };
}

/** `63 / 118` — spaces around the slash, drawn tabular so the two rows' digits line up. */
export function dayFraction(done: number, total: number): string {
  return `${done} / ${total}`;
}

/** The カード row's note: what is left, or `完了` once nothing is. */
export function cardsNote(day: DayProgress): string {
  const left = Math.max(0, day.cardsTotal - day.cardsDone);
  return left === 0 ? "完了" : `残り${left}枚`;
}

/**
 * The 復習 row's note: the pass/fail tally while the day is running, the day's accuracy once it is
 * done.
 *
 * Returned as parts rather than a string because the two numbers are drawn in different inks — the
 * passes in body ink, the fails in accent — and a single string would have to be split again by
 * whatever renders it.
 */
export function reviewsNote(day: DayProgress): { passes: number; fails: number } | string {
  const left = Math.max(0, day.cardsTotal - day.cardsDone);
  const answered = day.passes + day.fails;
  // Only once the *cards* are done, not once the reviews are: `reviewsTotal` is an estimate that
  // becomes exact as the day closes, so it can equal `passes + fails` momentarily mid-afternoon.
  if (left === 0 && answered > 0) {
    return `正解${((day.passes / answered) * 100).toFixed(1)}%`;
  }
  return { passes: day.passes, fails: day.fails };
}

/**
 * Which string sits in each of the header's three note slots.
 *
 * The 50px bar had **one** corner and a precedence rule for it — the countdown outranked
 * `Escで終了`, because the more urgent thing wins. The 72px bar has three places to put something,
 * so the rule is written out rather than inferred:
 *
 * - the **countdown belongs to the sitting**, so it rides the 復習 row, which is the row about work
 *   rather than about the day's shape;
 * - **`記録できませんでした` outranks everything** and takes the カード row, because an answer that
 *   was not recorded is the most urgent thing this header can say;
 * - and it **displaces `Escで終了`**, which is the one thing here that is only ever a reminder. That
 *   is not merely a priority call: the notice needs 132px against a note slot's 104, so the corner
 *   is where the room comes from.
 */
export function noteSlots(input: {
  notice?: string | null;
  countdown?: string | null;
  day: DayProgress;
  finished: boolean;
}): {
  cardNote: string;
  /** Whether that note is the failure notice rather than the day's own figure.
   *
   *  Returned rather than inferred from the string, because both things a renderer needs to know
   *  follow from it and neither is guessable: the slot widens to 140px, and it is the one place on
   *  this header that is a live region. Sniffing the text for length would be a rule that breaks
   *  the day somebody writes a longer deck note. */
  cardNoteUrgent: boolean;
  reviewNote: { passes: number; fails: number } | string;
  /** Whether the 復習 note is the idle countdown rather than the day's tally.
   *
   *  Same reason `cardNoteUrgent` is returned rather than sniffed: the renderer needs to know, and
   *  the only other way to tell is to guess from the text. It also keeps the countdown findable —
   *  it had its own slot and its own hook before the header grew a second row, and losing that
   *  would have made the one behaviour no pure test can reach unassertable. */
  reviewNoteCountdown: boolean;
  corner: string | null;
} {
  const urgent = Boolean(input.notice);
  return {
    cardNote: input.notice || cardsNote(input.day),
    cardNoteUrgent: urgent,
    reviewNote: input.countdown || reviewsNote(input.day),
    reviewNoteCountdown: Boolean(input.countdown),
    corner: urgent || input.finished ? null : "Escで終了",
  };
}

/**
 * `新規4 · 要復習71`, omitting a zero rather than printing it.
 *
 * **The number follows the word**, which is the order the canvas draws (`新規4 · 要復習71`) and the
 * opposite of the English it replaces. Japanese counts read that way round — the label names what
 * is being counted and the figure completes it — and it is also what keeps the two terms
 * distinguishable at a glance in a mono font, where `4 新規 · 71 要復習` puts two numerals at the
 * ragged left edge of a string that is scanned from the right.
 */
export function queueSummary(counts: Pick<ReviewCounts, "new" | "learning" | "review">): string {
  const due = counts.learning + counts.review;
  const parts: string[] = [];
  if (counts.new) parts.push(`新規${counts.new}`);
  if (due) parts.push(`要復習${due}`);
  return parts.join(" · ") || "なし";
}

/** How long the 簡 seal stays up, animation included (日本-700). `index.css`'s `easy-seal` keyframes
 *  are timed against it, so the two change together. */
export const EASY_SEAL_MS = 1500;

/**
 * The reveal time printed on the 簡 seal: `1.2秒` (日本-700). One decimal, because the threshold is
 * set in tenths of a second and a figure coarser than the setting cannot say how close it was.
 */
export function easySealSeconds(revealMs: number): string {
  return `${(Math.max(0, revealMs) / 1000).toFixed(1)}秒`;
}

/** `19:40`. Minutes and seconds, because a session is never hours and `0:07` reads wrong. */
export function elapsed(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

// --- tags vs part of speech ----------------------------------------------------------------------

export interface TagChip {
  /** `jlpt`, `freq`, `source` — drawn small and dim ahead of the value. Empty for a hand-made tag. */
  namespace: string;
  value: string;
  raw: string;
  /** Leeches are the one tag the design tints, and it is not an error state — see `7d`. */
  leech: boolean;
}

/**
 * A tag to its chip (frame `7c`).
 *
 * **Machine tags keep their namespace, visibly**, so a hand-made tag is tellable from a generated
 * one at a glance. Two spellings exist in this collection and both are namespaces: `JLPT_N3` from
 * the enrichment engine (tags cannot contain spaces, hence the underscore) and `freq::top10k` in
 * Anki's own hierarchy separator. Splitting on either is what stops `JLPT_N3` rendering as one
 * opaque word beside a properly split `freq · top10k`.
 */
export function tagChip(raw: string): TagChip {
  const leech = raw.toLowerCase() === "leech";
  const hierarchical = raw.split("::");
  const leaf = hierarchical[hierarchical.length - 1];
  if (hierarchical.length > 1 && leaf) {
    return {
      namespace: hierarchical.slice(0, -1).join("::").toLowerCase(),
      value: leaf,
      raw,
      leech,
    };
  }
  // A single colon, AFTER `::` has been tried — this collection's frequency tags are `Freq:Top_1k`,
  // a third spelling the engine writes and neither of the other two branches catches. Found by
  // looking at a real card: the chip rendered as one opaque word beside a properly split `jlpt N3`.
  const colon = /^([A-Za-z]+):(.+)$/.exec(raw);
  if (colon?.[1] && colon[2]) {
    return { namespace: colon[1].toLowerCase(), value: colon[2], raw, leech };
  }
  const underscored = /^([A-Za-z]+)_(.+)$/.exec(raw);
  if (underscored?.[1] && underscored[2]) {
    return { namespace: underscored[1].toLowerCase(), value: underscored[2], raw, leech };
  }
  return { namespace: "", value: raw, raw, leech };
}

/**
 * Part of speech is a **field**, not a tag, so it never becomes a chip.
 *
 * The engine writes it one line per sense separated by a bare `\n` — the same convention
 * `format_english_and_pos` uses for `English`, which renders only because the card template carries
 * `white-space: pre-line`. Here it is drawn as italic text under the gloss, so the newlines are
 * turned into a middle dot rather than relying on a template this app does not own.
 */
export function partOfSpeechLine(value: string): string {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .join(" · ");
}


// --- the idle timeout ----------------------------------------------------------------------------

export const COUNTDOWN_VISIBLE_SECONDS = 60;
/**
 * How late the countdown appears.
 *
 * **Not from the start of the window**, deliberately. A clock counting down from 5:00 on every card
 * is a pressure timer during study, and since it resets on every action it would spend the whole
 * session flicking back to 5:00 — motion in the corner of the eye that says nothing. You only need
 * it when you are close to losing the sitting.
 */

/** `残り 0:42`, or null while there is nothing worth saying. */
export function idleCountdown(secondsLeft: number): string | null {
  if (secondsLeft > COUNTDOWN_VISIBLE_SECONDS) return null;
  return `残り ${elapsed(Math.max(0, secondsLeft))}`;
}

// --- the answer face's shape (日本-350) ------------------------------------------------------------

export type AnswerLayout = "column" | "split";

/**
 * One column or two — **the width decides it, and nothing else** (Alex, 16 Aug 2026).
 *
 * This reverses the handoff's fallback, which it shipped with: *a card with no images uses the
 * single centred column at any width, because two columns with an empty one is worse than the
 * layout it replaced.* That was right while an empty image column really was empty. It is not the
 * choice on offer any more — an image-less card now draws a **placeholder** in the right column, so
 * the width of the text column stops depending on what the card happens to carry, and a run of
 * cards no longer reflows between two shapes as their pictures come and go.
 *
 * `imageCount` is therefore no longer read. It stays in the signature because the caller has it and
 * because the decision it used to make is exactly the one somebody will try to put back; the
 * reasoning above is here so they find out why first.
 *
 * Below the breakpoint nothing changes, and that is a claim rather than a concession: the single
 * column is the right layout for a half-screen window and for the phone, and keeping one shape
 * shared between them is worth more than a second arrangement at 900px.
 */
export function answerLayout(wide: boolean, _imageCount: number): AnswerLayout {
  return wide ? "split" : "column";
}

// --- marking the card's word inside its example sentence (日本-279) ----------------------------

/**
 * Split `sentence` on `target`, alternating: even indices are plain, **odd indices are matches**.
 *
 * `["一瞬", "躊躇っ", "たが、結局引き受けた。"]` — so a renderer maps the array and marks every
 * other item, with no index arithmetic and no second pass to work out which is which.
 *
 * **Every occurrence is marked, not just the first** (Alex, 23 Aug 2026). 65 of the collection's
 * 8,160 sentences contain their target more than once — 翻訳 appears three times in its own — and
 * marking one of three is a quiet claim that the other two are a different word.
 *
 * **The empty target has to be guarded, and it is not a formality**: `"abc".split("")` is
 * `["a","b","c"]`, so the natural implementation turns a card with no target — 3.7% of the
 * collection, the normal case for a sentence that carries no form of its own word — into one
 * marked character per glyph.
 *
 * No regex anywhere, which is what makes the target safe to use unescaped: it is arbitrary text off
 * a note, `String.prototype.split` takes it literally, and `(` or `.` in a field can therefore
 * never become a pattern.
 */
export function splitOnTarget(sentence: string, target: string): string[] {
  if (!sentence || !target) return [sentence];

  const parts = sentence.split(target);
  if (parts.length === 1) return [sentence];

  // Rebuild as plain/match/plain/match/… — `split` drops the separators, and the separator is the
  // one thing the caller needs back.
  const out: string[] = [];
  parts.forEach((part, index) => {
    if (index > 0) out.push(target);
    out.push(part);
  });
  return out;
}

